import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { FormatDate } from '@/ui-kit/atoms/format-date';
import { type CustomerSource, customerDetailPath } from '@/app-kit/customer-source';
import type { OrderCommissionRow } from '@/app-kit/use-orders';
import { SortTh } from '@/ui-kit/molecules/sort-header';
import { useSortedRows } from '@/app-kit/use-table-sort';
import type { SortState } from '@/app-kit/table-sort';

/*
 * The per-run commission payout block, shared by the two surfaces that show
 * "who got paid on this order" (#1213):
 *
 *   - Customer → Order → Commissions tab  (one block per accepted run)
 *   - Commissions → Run → Order Detail    (exactly one run)
 *
 * Extracted rather than duplicated because these two drifted once already:
 * #1213 part 1 added the USD column to the customer tab, and the run page —
 * showing an aggregated Exigo-style rollup — silently kept displaying raw
 * local-currency amounts, which is what part 2 of that issue reported.
 */

// ── table primitives (shared with order-detail-page) ────────────────────────

const TH_CLASS = 'border-b border-gray-200 px-3 py-2 text-left text-xs font-medium uppercase text-gray-600';
export function Th({ children, className }: { children: ReactNode; className?: string }) {
  return <th className={clsx(TH_CLASS, className)}>{children}</th>;
}
/** `SortableTh`'s props, rendered as a plain `Th` — for a block that does not sort. */
function StaticTh({ children, className }: { children: ReactNode; className?: string; col: string; sort: unknown; onToggle: unknown }) {
  return <Th className={className}>{children}</Th>;
}
/** `Th` as a click-to-sort heading. */
export function SortableTh<K extends string>({ children, className, col, sort, onToggle }: {
  children: ReactNode; className?: string; col: K; sort: SortState<K>; onToggle: (col: K) => void;
}) {
  return <SortTh col={col} sort={sort} onToggle={onToggle} className={clsx(TH_CLASS, className)}>{children}</SortTh>;
}
/**
 * A heading on two lines, so the narrow amount columns keep their width. The
 * lines centre on each other; the heading sits right, over the numbers.
 */
function Stacked({ top, bottom }: { top: string; bottom: string }) {
  return <span className="inline-block text-center"><span className="block">{top}</span> <span className="block">{bottom}</span></span>;
}
export function Td({ children, className, title }: { children: ReactNode; className?: string; title?: string }) {
  return <td className={clsx('px-3 py-2', className)} title={title}>{children}</td>;
}
export function Muted({ children }: { children: ReactNode }) {
  return <span className="text-gray-400">{children}</span>;
}

/**
 * Two decimal places for display, independent of source scale — the
 * underlying numerics carry 4. Blank rather than "NaN" for absent values.
 */
export function decimal2(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : '—';
}

/**
 * A percentage to two decimal places with trailing zeros dropped: 3.0000 →
 * "3", 7.5000 → "7.5", 2.9999 → "3", 9.8837 → "9.88". Blank for absent values.
 */
export function percent(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? String(Number(n.toFixed(2))) : '—';
}

// ── ordering + totals ───────────────────────────────────────────────────────

/**
 * Bonus-type display order (#997): each payout sits directly next to its
 * matching Health-Start (`HS…`) deduction — "black, red, black, red" — rather
 * than all payouts then all deductions. Types not listed here keep their
 * server order and sort after the enumerated pairs.
 */
const BONUS_TYPE_ORDER = [
  'SP', 'HSSP',
  'PPC', 'HSPPC',
  'QSC', 'HSQSC',
  'SCC', 'HSSCC',
  'PB1', 'HSPB1',
  'PB2', 'HSPB2',
  'PB3', 'HSPB3',
  'PB4', 'HSPB4',
  'PB5', 'HSPB5',
  'POB', 'HSPOB',
];
export const bonusTypeRank = (t: string | null | undefined): number => {
  const i = t ? BONUS_TYPE_ORDER.indexOf(t) : -1;
  return i === -1 ? BONUS_TYPE_ORDER.length : i;
};

/**
 * Net commission per currency for one run's payout rows. Amounts in different
 * currencies are never summed together — a run can pay earners in several.
 */
export function netByCurrency(rows: OrderCommissionRow[]): Array<[string, number]> {
  const byCcy = new Map<string, number>();
  for (const r of rows) byCcy.set(r.currency_code, (byCcy.get(r.currency_code) ?? 0) + r.amount);
  return [...byCcy.entries()];
}

/**
 * Net commission in USD across every row, regardless of local currency (#1213).
 * This is the one total that answers "what did this run pay on this order" —
 * the per-currency nets above cannot be added to each other.
 */
export function netUsd(rows: OrderCommissionRow[]): number {
  return rows.reduce((sum, r) => sum + r.amount_usd, 0);
}

/**
 * Net commission in the order's currency, or null when it cannot be stated
 * whole: a line with no amount in the order's currency, or lines in more than
 * one order currency.
 */
export function netOrder(rows: OrderCommissionRow[]): [string, number] | null {
  const codes = new Set(rows.map((r) => r.order_currency_code));
  if (codes.size !== 1 || rows.some((r) => r.amount_order === null)) return null;
  return [rows[0]!.order_currency_code, rows.reduce((sum, r) => sum + r.amount_order!, 0)];
}

// ── the block ───────────────────────────────────────────────────────────────

export type OrderPayoutsTableProps = {
  /** One run's payout rows, pre-filtered. Rendered in bonus-pair order. */
  rows: OrderCommissionRow[];
  /** Customer tree the "Paid to" links resolve against. */
  source: CustomerSource;
  /**
   * Appended after the run description in the block header. The customer tab
   * uses it for a `run N` link across to the run-scoped page; the run page
   * itself passes nothing, having no useful destination to link to.
   */
  headerSuffix?: ReactNode;
  /**
   * Click-to-sort headings. Only the customer → order Commissions tab sorts;
   * the run-scoped order page shares this block and keeps its fixed order.
   */
  sortable?: boolean;
};

export function OrderPayoutsTable({ rows, source, headerSuffix, sortable = false }: OrderPayoutsTableProps) {
  // Opens in bonus-pair order: Bonus Type sorted by its pair RANK, not
  // alphabetically, so that heading shows as sorted. The sort is stable, so
  // rows sharing a bonus type keep the server's ordering within the pair.
  const sorted = useSortedRows(rows, {
    bonus_type:  (p) => bonusTypeRank(p.bonus_type),
    paid_to:     (p) => p.customer_name,
    customer_id: (p) => p.customer_id,
    rank:        (p) => p.rank_code,
    level:       (p) => p.level,
    paid_level:  (p) => p.paid_level,
    base_order:  (p) => p.base_order,
    percentage:  (p) => (p.percentage == null ? null : Number(p.percentage)),
    amount_order: (p) => p.amount_order,
    amount:      (p) => p.amount,
    amount_usd:  (p) => p.amount_usd,
  }, { key: 'bonus_type', dir: 'asc' });
  const payouts = sorted.rows ?? [];
  const sh = { sort: sorted.sort, onToggle: sorted.toggle };
  // Unsortable: a plain heading. The rows are still in bonus-pair order — the
  // sort's default, which a table with no clickable heading never leaves.
  const H = sortable ? SortableTh : StaticTh;
  const first = payouts[0];
  const perCurrency = netByCurrency(payouts);
  const orderNet = netOrder(payouts);

  return (
    <>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-100 pb-2">
        <h3 className="text-sm font-semibold text-gray-900">
          {first?.commission_run_description ?? `Run ${first?.commission_run_id ?? ''}`}
          {headerSuffix}
        </h3>
        <span className="flex items-baseline gap-3 text-xs text-gray-500">
          {/* Net across the run's bonuses. With reversals and clawbacks in
              play, per-bonus rows alone don't answer "what did this run
              actually pay". Only summed within a single currency — mixed
              currencies are not added together. */}
          {orderNet && (
            <span className={clsx('font-medium tabular-nums', orderNet[1] < 0 && 'text-red-600')}>
              Order net {decimal2(orderNet[1])} {orderNet[0]}
            </span>
          )}
          {perCurrency.map(([code, net]) => (
            <span key={code} className={clsx('tabular-nums', net < 0 && 'text-red-600')}>
              Partner net {decimal2(net)} {code}
            </span>
          ))}
          {/* #1213: the only total that spans the partners' currencies. Shown
              whenever the run paid in more than one, where the per-currency
              nets beside it cannot be added up. */}
          {perCurrency.length > 1 && (
            <span className={clsx('font-medium tabular-nums', netUsd(payouts) < 0 && 'text-red-600')}>
              USD net {decimal2(netUsd(payouts))}
            </span>
          )}
          {first?.accepted_date && (
            <span>Accepted <FormatDate date={first.accepted_date} format="date" /></span>
          )}
        </span>
      </div>
      {/* One flat row per earner — bonus carried as a column rather than a
          spanning header, so the whole run reads as a single sortable-looking
          grid with no interstitial rows to scan past. */}
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <H {...sh} col="bonus_type" className="w-24">Bonus Type</H>
            {/* nowrap: the run-scoped page is narrower than the customer tab,
                enough that a three-part name wrapped to three lines and
                tripled the row height. */}
            <H {...sh} col="paid_to" className="whitespace-nowrap">Paid to</H>
            <H {...sh} col="customer_id" className="w-28">Customer ID</H>
            <H {...sh} col="rank" className="w-24">Rank</H>
            {/* Both levels are shown: the same earner can take several rows
                on one bonus differing ONLY by paid_level (observed: level 1
                at paid_levels 20/30/40), which without this column render as
                identical duplicates. */}
            <H {...sh} col="level" className="w-16 text-right">Level</H>
            <H {...sh} col="paid_level" className="w-20 text-right">Paid lvl</H>
            <H {...sh} col="base_order" className="w-28 text-right"><Stacked top="Base" bottom="(Order)" /></H>
            <H {...sh} col="percentage" className="w-16 text-right">%</H>
            <H {...sh} col="amount_order" className="w-28 text-right"><Stacked top="Amount" bottom="(Order)" /></H>
            <H {...sh} col="amount" className="w-28 text-right"><Stacked top="Amount" bottom="(Partner)" /></H>
            <H {...sh} col="amount_usd" className="w-24 text-right"><Stacked top="Amount" bottom="(USD)" /></H>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {payouts.map((p, i) => (
              <tr key={`${p.bonus_id}-${p.customer_id}-${p.level}-${p.paid_level}-${i}`}>
                <Td className="font-mono" title={p.bonus_name ?? undefined}>
                  {p.bonus_type ?? <Muted>—</Muted>}
                </Td>
                <Td className="whitespace-nowrap">
                  <Link
                    to={customerDetailPath(source, p.customer_id)}
                    className="text-brand-600 hover:underline"
                  >
                    {p.customer_name || `Customer ${p.customer_id}`}
                  </Link>
                </Td>
                <Td className="font-mono">{p.customer_id}</Td>
                <Td className="font-mono">{p.rank_code || <Muted>—</Muted>}</Td>
                <Td className="text-right tabular-nums">{p.level ?? <Muted>—</Muted>}</Td>
                <Td className="text-right tabular-nums">{p.paid_level ?? <Muted>—</Muted>}</Td>
                {/* What the percentage is paid on, in the order's currency. On a
                    payout line % × base = Amount (Order); a clawback line does
                    not follow it. */}
                <Td className={clsx('text-right tabular-nums', p.base_order != null && p.base_order < 0 && 'text-red-600')}>
                  {p.base_order != null ? `${decimal2(p.base_order)} ${p.order_currency_code}` : <Muted>—</Muted>}
                </Td>
                <Td className="text-right tabular-nums">
                  {p.percentage != null ? `${percent(p.percentage)}%` : <Muted>—</Muted>}
                </Td>
                {/* The order's currency, the same on every row, so the partners'
                    shares of one order compare directly. Reversals and
                    clawbacks are negative and common, so the sign is coloured
                    to stop a reversal reading as a payment. */}
                <Td className={clsx('text-right tabular-nums', p.amount_order != null && p.amount_order < 0 && 'text-red-600')}>
                  {p.amount_order != null ? `${decimal2(p.amount_order)} ${p.order_currency_code}` : <Muted>—</Muted>}
                </Td>
                {/* What the partner was actually paid, in their own currency —
                    one order can pay partners in several (EUR, USD and CHF). */}
                <Td className={clsx('text-right tabular-nums', p.amount < 0 && 'text-red-600')}>
                  {decimal2(p.amount)} {p.currency_code}
                </Td>
                {/* #1213: the USD equivalent, so a multi-currency order can be
                    compared against Exigo (which shows this figure) and summed
                    at all. */}
                <Td className={clsx('text-right tabular-nums', p.amount_usd < 0 && 'text-red-600')}>
                  {decimal2(p.amount_usd)}
                </Td>
              </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
